import "@/components/oprema/Oprema.css";
import { useCallback, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/ui/design-system";
import { Feedback, useResource } from "@/components/workflow/shared";
import { PhotoPicker } from "@/components/oprema/PrimopredajaStep";
import { HandoverNotice } from "@/components/oprema/HandoverNotice";
import {
  equipmentApi,
  emptyEvidence,
  equipmentErrorMessage,
  type EquipmentProposal,
  type HandoverNotice as Notice,
  type InventoryWorker,
} from "@/lib/oprema";
import { useDanas } from "@/lib/dan";
import { datum } from "@/lib/format";

export function EquipmentInventoryPage() {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  return (
    <Inventory key={id} siteId={id} initialWorker={params.get("radnik")} />
  );
}
function Inventory({
  siteId,
  initialWorker,
}: {
  siteId: string;
  initialWorker: string | null;
}) {
  const { can } = useAuth();
  const read = can("oprema", "read");
  const create = can("oprema", "create");
  const resource = useResource(
    `${siteId}:${read}`,
    useCallback(
      (signal) =>
        read ? equipmentApi.inventory(siteId, signal) : Promise.resolve([]),
      [siteId, read],
    ),
  );
  const [selected, setSelected] = useState(initialWorker);
  const [completed, setCompleted] = useState<string[]>([]);
  const [notices, setNotices] = useState<Notice[]>([]);
  const [busy, setBusy] = useState(false);
  const workers = [...(resource.data ?? [])].sort(
    (a, b) =>
      Number(a.status === "popisan" || completed.includes(a.radnik_id)) -
        Number(b.status === "popisan" || completed.includes(b.radnik_id)) ||
      a.ime.localeCompare(b.ime, "hr"),
  );
  const listed = workers.filter(
    (worker) =>
      worker.status === "popisan" || completed.includes(worker.radnik_id),
  ).length;
  if (!read || !create)
    return <p role="alert">Nemate ovlast za popis opreme.</p>;
  const current = workers.find((worker) => worker.radnik_id === selected);
  return (
    <div className="avm-equipment mx-auto max-w-3xl space-y-4">
      <PageHeader
        title="Početno stanje"
        meta="Popišite opremu koju radnik već ima."
      />
      <Link
        className="inline-flex min-h-11 items-center underline"
        to={`/gradilisni-spisak/${siteId}?kartica=oprema`}
      >
        Natrag na opremu gradilišta
      </Link>
      <div className="grid grid-cols-2 gap-3">
        <div className="rounded-lg border border-green-300 bg-green-50 p-4">
          Popisano <strong>{listed}</strong>
        </div>
        <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
          Nije popisano <strong>{workers.length - listed}</strong>
        </div>
      </div>
      <Feedback error={resource.error} />
      {resource.error && (
        <Button variant="outline" onClick={resource.reload}>
          Pokušaj ponovno
        </Button>
      )}
      {resource.loading && <p role="status">Učitavanje radnika…</p>}
      {notices.map((notice) => (
        <HandoverNotice
          key={notice.receipt.operation_id}
          notice={notice}
          onUndone={() => {
            setCompleted((previous) =>
              previous.filter((id) => id !== notice.workerId),
            );
            resource.reload();
          }}
        />
      ))}
      <ul className="divide-y rounded-lg border bg-white">
        {workers.map((worker) => (
          <li key={worker.radnik_id}>
            <button
              className="min-h-14 w-full p-4 text-left"
              aria-expanded={selected === worker.radnik_id}
              disabled={busy}
              onClick={() => setSelected(worker.radnik_id)}
            >
              <strong>{worker.ime}</strong> ·{" "}
              {worker.status === "popisan" ||
              completed.includes(worker.radnik_id)
                ? `✓ Popisan${worker.datum ? ` ${datum(worker.datum)}` : ""} · dopuni`
                : "✗ Nije popisan"}
            </button>
          </li>
        ))}
      </ul>
      {current && (
        <InventoryCard
          key={current.radnik_id}
          worker={current}
          siteId={siteId}
          onBusy={setBusy}
          onSaved={(receipts) => {
            setNotices(receipts);
            setCompleted((previous) => [...previous, current.radnik_id]);
            const next = workers.find(
              (worker) =>
                worker.radnik_id !== current.radnik_id &&
                worker.status !== "popisan" &&
                !completed.includes(worker.radnik_id),
            );
            setSelected(next?.radnik_id ?? null);
            resource.reload();
          }}
        />
      )}
    </div>
  );
}
function InventoryCard({
  worker,
  siteId,
  onSaved,
  onBusy,
}: {
  worker: InventoryWorker;
  siteId: string;
  onSaved: (notices: Notice[]) => void;
  onBusy: (busy: boolean) => void;
}) {
  const today = useDanas();
  const resource = useResource(
    worker.radnik_id,
    useCallback(
      (signal) => equipmentApi.proposal(worker.radnik_id, siteId, signal),
      [worker.radnik_id, siteId],
    ),
  );
  const [choices, setChoices] = useState<
    Record<string, { month: string; size: string }>
  >({});
  const [photos, setPhotos] = useState<File[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState<string[]>([]);
  const running = useRef(false);
  const receipts = useRef<Notice[]>([]);
  const items = resource.data?.filter((item) => item.nacin !== "zaliha") ?? [];
  const selected = items.filter((item) => choices[item.vrsta_ozo_id]);
  function toggle(item: EquipmentProposal) {
    setChoices((previous) => {
      const next = { ...previous };
      if (next[item.vrsta_ozo_id]) delete next[item.vrsta_ozo_id];
      else
        next[item.vrsta_ozo_id] = {
          month: today.slice(0, 7),
          size: item.velicina ?? "",
        };
      return next;
    });
  }
  async function submit() {
    if (running.current || !selected.length) return;
    running.current = true;
    setBusy(true);
    onBusy(true);
    setError("");
    const months = [
      ...new Set(selected.map((item) => choices[item.vrsta_ozo_id]!.month)),
    ].sort();
    try {
      for (const month of months) {
        const pending = selected.filter(
          (item) =>
            choices[item.vrsta_ozo_id]!.month === month &&
            !sent.includes(item.vrsta_ozo_id),
        );
        if (!pending.length) continue;
        const receipt = await equipmentApi.issue(
          worker.radnik_id,
          {
            vrsta: "pocetno_stanje",
            gradiliste_id: siteId,
            datum: `${month}-01`,
            stavke: pending.map((item) => ({
              vrsta_ozo_id: item.vrsta_ozo_id,
              velicina: choices[item.vrsta_ozo_id]!.size.trim() || null,
              kolicina: 1,
            })),
          },
          { ...emptyEvidence(), photos },
        );
        receipts.current.push({ workerId: worker.radnik_id, receipt });
        setSent((previous) => [
          ...previous,
          ...pending.map((item) => item.vrsta_ozo_id),
        ]);
      }
      if (receipts.current.length) onSaved([...receipts.current]);
    } catch (cause) {
      setError(equipmentErrorMessage(cause, "Početno stanje nije spremljeno."));
    } finally {
      running.current = false;
      setBusy(false);
      onBusy(false);
    }
  }
  return (
    <section className="space-y-4 rounded-lg border border-blue-300 bg-blue-50 p-4">
      <h2 className="text-lg font-semibold">{worker.ime}</h2>
      <p>Sljedeći korak: označite što radnik ima i kada je dobio.</p>
      {worker.stavke.length > 0 && (
        <details>
          <summary className="min-h-11 cursor-pointer">
            Trenutna oprema ({worker.stavke.length})
          </summary>
          <ul>
            {worker.stavke.map((item) => (
              <li key={item.id}>
                {item.vrsta_ozo_naziv ?? item.vrsta_izvorni_tekst} ·{" "}
                {item.velicina ?? "Bez veličine"} ·{" "}
                {datum(item.datum_zaduzenja)}
              </li>
            ))}
          </ul>
        </details>
      )}
      <Feedback error={error || resource.error} />
      {resource.loading && <p role="status">Učitavanje vrsta opreme…</p>}
      <fieldset disabled={busy} className="space-y-3">
        {items.map((item) => {
          const choice = choices[item.vrsta_ozo_id];
          const done = sent.includes(item.vrsta_ozo_id);
          return (
            <div key={item.vrsta_ozo_id} className="space-y-2">
              <button
                className={`min-h-11 rounded-full border px-4 ${choice ? "border-green-400 bg-green-100" : "bg-white"}`}
                type="button"
                aria-pressed={Boolean(choice)}
                disabled={done}
                onClick={() => toggle(item)}
              >
                {choice ? "✓ " : "+ "}
                {item.naziv}
                {done ? " · upisano" : ""}
              </button>
              {choice && (
                <div className="flex flex-wrap gap-3">
                  <label>
                    Dobio (mjesec/godina): {item.naziv}
                    <Input
                      className="min-h-11"
                      type="month"
                      min="2026-01"
                      max={today.slice(0, 7)}
                      value={choice.month}
                      disabled={done}
                      onChange={(event) =>
                        setChoices((previous) => ({
                          ...previous,
                          [item.vrsta_ozo_id]: {
                            ...choice,
                            month: event.target.value,
                          },
                        }))
                      }
                    />
                  </label>
                  <label>
                    Veličina: {item.naziv}
                    <Input
                      className="min-h-11"
                      maxLength={40}
                      value={choice.size}
                      disabled={done}
                      onChange={(event) =>
                        setChoices((previous) => ({
                          ...previous,
                          [item.vrsta_ozo_id]: {
                            ...choice,
                            size: event.target.value,
                          },
                        }))
                      }
                    />
                  </label>
                </div>
              )}
            </div>
          );
        })}
        <PhotoPicker
          photos={photos}
          onChange={setPhotos}
          optional
          disabled={busy}
        />
      </fieldset>
      {sent.length > 0 && error && (
        <p>Već upisane stavke neće se ponovno poslati.</p>
      )}
      <Button
        className="min-h-12 w-full"
        disabled={
          busy ||
          !selected.length ||
          selected.some(
            (item) =>
              !/^\d{4}-(0[1-9]|1[0-2])$/.test(
                choices[item.vrsta_ozo_id]!.month,
              ) ||
              choices[item.vrsta_ozo_id]!.month < "2026-01" ||
              choices[item.vrsta_ozo_id]!.month > today.slice(0, 7),
          )
        }
        onClick={() => void submit()}
      >
        {busy ? "Upisivanje…" : "Spremi i sljedeći"}
      </Button>
    </section>
  );
}
